import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailRailJunction,
  InsertHrailRailJunctionBody,
  UpdateHrailRailJunctionBody,
} from "./types/railJunctions";

export const useHrailRailJunctionsApi = () => {
  const api = useApi();

  const getAdminHrailRailJunction = useCallback(
    (railJunctionId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/rail_junctions/${railJunctionId}`;
      return useSWRMutation<HrailRailJunction, CustomError, string>(url, (url) =>
        api.get<HrailRailJunction>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailRailJunctions = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rail_junctions`, query);
      return useSWRImmutable<Paginated<HrailRailJunction>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailRailJunction>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailRailJunctions = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rail_junctions`, query);
      return api.get<Paginated<HrailRailJunction>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailRailJunction = useCallback(
    async (body: InsertHrailRailJunctionBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailRailJunctionBody>(
        `/admin/settings/hrail/rail_junctions`,
        body,
      );
      mutateCache([`/admin/settings/hrail/rail_junctions`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailRailJunction = useCallback(
    async (railJunctionId: string, body: UpdateHrailRailJunctionBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_junctions/${railJunctionId}`;
      await api.put<UpdateHrailRailJunctionBody>(url, body);
      mutateCache([`/admin/settings/hrail/rail_junctions`], url);
    },
    [api],
  );

  const updateAdminHrailRailJunctionVisibility = useCallback(
    async (railJunctionId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_junctions/${railJunctionId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/rail_junctions`]);
    },
    [api],
  );

  const updateAdminHrailRailJunctionOrder = useCallback(
    async (railJunctionId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_junctions/${railJunctionId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/rail_junctions`]);
    },
    [api],
  );

  const deleteAdminHrailRailJunction = useCallback(
    async (railJunctionId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/rail_junctions/${railJunctionId}`);
      mutateCache([`/admin/settings/hrail/rail_junctions`]);
    },
    [api],
  );

  return {
    getAdminHrailRailJunction,
    useAdminHrailRailJunctions,
    getAdminHrailRailJunctions,
    insertAdminHrailRailJunction,
    updateAdminHrailRailJunction,
    updateAdminHrailRailJunctionVisibility,
    updateAdminHrailRailJunctionOrder,
    deleteAdminHrailRailJunction,
  };
};
